import React, { useMemo } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
  Avatar,
  Card,
  CardContent,
  Grid,
  Button,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { OutfitRowData } from '../../types/admin';
import { ClothingItem } from '../../types';
import {
  GENDER_CATEGORIES,
  FASHION_BRANDS,
  getClothingTypesByGender,
} from '../../data/fashionCategories';
import { FASHION_COLORS, getColorValue } from '../../data/fashionColors';

const EVENT_TYPES = [
  'Casual',
  'Business Casual',
  'Formal',
  'Wedding',
  'Date Night',
  'Party',
  'Graduation',
  'Interview',
  'Vacation',
  'Beach',
  'Workout',
  'Festival',
];

interface OutfitRowProps {
  row: OutfitRowData;
  rowIndex: number;
  onRowUpdate: (index: number, updates: Partial<OutfitRowData>) => void;
  onRowRemove: (index: number) => void;
  onClothingItemAdd: (rowIndex: number) => void;
  onClothingItemRemove: (rowIndex: number, itemIndex: number) => void;
  onClothingItemUpdate: (
    rowIndex: number,
    itemIndex: number,
    updates: Partial<ClothingItem>
  ) => void;
}

/**
 * Renders the upload status indicator for an outfit row.
 */
const UploadStatusIndicator: React.FC<{
  status: OutfitRowData['uploadStatus'];
  error?: string;
}> = ({ status, error }) => {
  switch (status) {
    case 'uploading':
      return (
        <Tooltip title="Uploading...">
          <CircularProgress size={20} sx={{ color: 'primary.main' }} />
        </Tooltip>
      );
    case 'success':
      return (
        <Tooltip title="Upload successful">
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
        </Tooltip>
      );
    case 'failed':
      return (
        <Tooltip title={error || 'Upload failed'}>
          <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Pending">
          <CloudUploadIcon sx={{ color: 'text.disabled', fontSize: 24 }} />
        </Tooltip>
      );
  }
};

/**
 * Renders a single clothing item as a compact inline form row.
 */
const ClothingItemRow: React.FC<{
  item: ClothingItem;
  itemIndex: number;
  rowIndex: number;
  onUpdate: (
    rowIndex: number,
    itemIndex: number,
    updates: Partial<ClothingItem>
  ) => void;
  onRemove: (rowIndex: number, itemIndex: number) => void;
  isLoading: boolean;
}> = ({ item, itemIndex, rowIndex, onUpdate, onRemove, isLoading }) => {
  const availableTypes = useMemo(
    () => (item.gender ? getClothingTypesByGender(item.gender) : []),
    [item.gender]
  );

  const handleGenderChange = (newGender: string) => {
    // When gender changes, reset type to empty
    onUpdate(rowIndex, itemIndex, { gender: newGender, type: '' as any });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <Skeleton variant="rounded" width={100} height={40} />
        <Skeleton variant="rounded" width={150} height={40} />
        <Skeleton variant="rounded" width={150} height={40} />
        <Skeleton variant="rounded" width={100} height={40} />
        <Skeleton variant="rounded" width={150} height={40} />
      </Box>
    );
  }

  return (
    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Grid item xs={12} sm={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Gender"
          value={item.gender || ''}
          onChange={(e) => handleGenderChange(e.target.value)}
        >
          {GENDER_CATEGORIES.map((g) => (
            <MenuItem key={g} value={g}>
              {g}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={2.5}>
        <Autocomplete
          size="small"
          options={availableTypes}
          value={item.type ? String(item.type) : ''}
          onChange={(_, val) =>
            onUpdate(rowIndex, itemIndex, { type: (val || '') as any })
          }
          renderInput={(params) => (
            <TextField {...params} label="Type" placeholder="Select type" />
          )}
          isOptionEqualToValue={(opt, val) => opt === val}
          disabled={!item.gender}
        />
      </Grid>
      <Grid item xs={12} sm={2.5}>
        <Autocomplete
          size="small"
          options={[...FASHION_BRANDS]}
          value={item.brand || ''}
          onChange={(_, val) =>
            onUpdate(rowIndex, itemIndex, { brand: val || '' })
          }
          freeSolo
          renderInput={(params) => (
            <TextField {...params} label="Brand" placeholder="Select brand" />
          )}
          isOptionEqualToValue={(opt, val) => opt === val}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Color"
          value={item.color || ''}
          onChange={(e) =>
            onUpdate(rowIndex, itemIndex, { color: e.target.value })
          }
          SelectProps={{
            MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
          }}
        >
          {FASHION_COLORS.map((c) => (
            <MenuItem key={c} value={c}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: getColorValue(c),
                    border:
                      c === 'White' || c === 'Cream'
                        ? '1px solid #ccc'
                        : 'none',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">{c}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={2}>
        <TextField
          fullWidth
          size="small"
          label="Description"
          value={item.description || ''}
          onChange={(e) =>
            onUpdate(rowIndex, itemIndex, { description: e.target.value })
          }
          placeholder="e.g., Slim fit"
        />
      </Grid>
      <Grid item xs={12} sm={1}>
        <Tooltip title="Remove clothing item">
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemove(rowIndex, itemIndex)}
            aria-label={`Remove clothing item ${itemIndex + 1}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

/**
 * OutfitRow renders a single row in the bulk upload data table as a Card.
 * Each row contains: thumbnail, event type, description, weather tag,
 * clothing items (as inline sub-forms), and action buttons.
 */
const OutfitRow: React.FC<OutfitRowProps> = ({
  row,
  rowIndex,
  onRowUpdate,
  onRowRemove,
  onClothingItemAdd,
  onClothingItemRemove,
  onClothingItemUpdate,
}) => {
  const isAiLoading = row.aiAnalysisStatus === 'loading';
  const isAiFailed = row.aiAnalysisStatus === 'failed';
  const hasValidationErrors =
    row.validationErrors && row.validationErrors.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderColor: hasValidationErrors ? 'error.main' : undefined,
      }}
    >
      <CardContent>
        {/* Top section: thumbnail + main fields + status + actions */}
        <Grid container spacing={2} alignItems="flex-start">
          {/* Thumbnail */}
          <Grid item xs="auto">
            <Avatar
              variant="rounded"
              src={row.previewUrl}
              alt={`Outfit ${rowIndex + 1}`}
              sx={{ width: 80, height: 80, '& img': { objectFit: 'contain' } }}
            />
          </Grid>

          {/* Event Type */}
          <Grid item xs={12} sm={2}>
            {isAiLoading ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <TextField
                select
                fullWidth
                size="small"
                label="Event Type"
                value={row.eventType}
                onChange={(e) =>
                  onRowUpdate(rowIndex, { eventType: e.target.value })
                }
                error={hasValidationErrors && !row.eventType}
              >
                {EVENT_TYPES.map((et) => (
                  <MenuItem key={et} value={et}>
                    {et}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>

          {/* Description */}
          <Grid item xs={12} sm={3}>
            {isAiLoading ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={row.description}
                onChange={(e) =>
                  onRowUpdate(rowIndex, { description: e.target.value })
                }
                placeholder="Outfit description"
              />
            )}
          </Grid>

          {/* Weather Tag */}
          <Grid item xs={12} sm={2.5}>
            {isAiLoading ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Weather Tag"
                value={row.weatherTag}
                onChange={(e) =>
                  onRowUpdate(rowIndex, { weatherTag: e.target.value })
                }
                placeholder="e.g., Summer, All-Season"
              />
            )}
          </Grid>

          {/* AI Status + Upload Status + Actions */}
          <Grid
            item
            xs={12}
            sm="auto"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 'auto',
            }}
          >
            {isAiLoading && (
              <Tooltip title="AI analysis in progress">
                <CircularProgress size={20} />
              </Tooltip>
            )}
            {isAiFailed && (
              <Tooltip title="AI analysis failed — fill fields manually">
                <WarningIcon sx={{ color: 'warning.main', fontSize: 24 }} />
              </Tooltip>
            )}

            <UploadStatusIndicator
              status={row.uploadStatus}
              error={row.uploadError}
            />

            <Tooltip title="Remove row">
              <IconButton
                color="error"
                onClick={() => onRowRemove(rowIndex)}
                aria-label={`Remove outfit row ${rowIndex + 1}`}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Validation errors */}
        {hasValidationErrors && (
          <Box sx={{ mt: 1 }}>
            {row.validationErrors!.map((err, i) => (
              <Typography key={i} variant="caption" color="error">
                {err}
              </Typography>
            ))}
          </Box>
        )}

        {/* Clothing Items Section */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Clothing Items ({row.clothingItems.length})
          </Typography>

          {row.clothingItems.map((item, itemIndex) => (
            <ClothingItemRow
              key={itemIndex}
              item={item}
              itemIndex={itemIndex}
              rowIndex={rowIndex}
              onUpdate={onClothingItemUpdate}
              onRemove={onClothingItemRemove}
              isLoading={isAiLoading}
            />
          ))}

          {/* Show skeletons when AI is loading and no items yet */}
          {isAiLoading && row.clothingItems.length === 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <Skeleton variant="rounded" width={100} height={40} />
              <Skeleton variant="rounded" width={150} height={40} />
              <Skeleton variant="rounded" width={150} height={40} />
              <Skeleton variant="rounded" width={100} height={40} />
              <Skeleton variant="rounded" width={150} height={40} />
            </Box>
          )}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onClothingItemAdd(rowIndex)}
            sx={{ mt: 0.5 }}
          >
            Add Clothing Item
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OutfitRow;
